/* ============================================================
   Header behavior
   - Cycling search placeholders (typewriter style)
   - Focus state class toggle
   - Mobile search overlay open/close
   - Cart count update on cart change events
   - Predictive search hook (Dawn ships /search/suggest.json)
   - Submit intercept: Enter navigates to top predictive result
     (Shopify /search cannot return collections, so we bypass it)
   ============================================================ */
(function () {
  'use strict';


  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  // ---------- Placeholder cycler ----------
  function initPlaceholderCycler() {
    const form = document.querySelector('.hsg-header__search');
    if (!form) return;
    const input = form.querySelector('[data-placeholder-cycle]');
    if (!input) return;


    let placeholders;
    try {
      placeholders = JSON.parse(form.dataset.placeholders || '[]');
    } catch (_) { placeholders = []; }
    if (!placeholders.length) return;


    if (reduceMotion) {
      input.placeholder = placeholders[0];
      return;
    }


    let phraseIndex = 0;
    let charIndex = 0;
    let typing = true;
    let paused = false;
    const TYPE_MS = 70;
    const ERASE_MS = 40;
    const HOLD_MS = 1800;


    function tick() {
      if (paused) return;
      const phrase = placeholders[phraseIndex];


      if (typing) {
        charIndex++;
        input.placeholder = phrase.slice(0, charIndex);
        if (charIndex === phrase.length) {
          typing = false;
          setTimeout(tick, HOLD_MS);
          return;
        }
        setTimeout(tick, TYPE_MS);
      } else {
        charIndex--;
        input.placeholder = phrase.slice(0, charIndex);
        if (charIndex === 0) {
          typing = true;
          phraseIndex = (phraseIndex + 1) % placeholders.length;
        }
        setTimeout(tick, ERASE_MS);
      }
    }


    // Pause cycling on focus / when value is present
    input.addEventListener('focus', () => { paused = true;  input.placeholder = ''; });
    input.addEventListener('blur',  () => {
      if (!input.value) { paused = false; charIndex = 0; typing = true; tick(); }
    });


    tick();
  }


  // ---------- Focus class on the search wrapper ----------
  function initSearchFocusClass() {
    const form = document.querySelector('.hsg-header__search');
    if (!form) return;
    const input = form.querySelector('input[type="search"]');
    input.addEventListener('focus', () => form.classList.add('is-focused'));
    input.addEventListener('blur',  () => form.classList.remove('is-focused'));
  }


  // ---------- Mobile search overlay ----------
  function initMobileSearch() {
    const toggle = document.querySelector('[data-search-toggle]');
    const overlay = document.querySelector('[data-mobile-search]');
    const close = document.querySelector('[data-mobile-search-close]');
    if (!toggle || !overlay) return;


    const open = () => {
      overlay.hidden = false;
      const input = overlay.querySelector('input[type="search"]');
      setTimeout(() => input && input.focus(), 50);
      document.documentElement.style.overflow = 'hidden';
    };
    const closeIt = () => {
      overlay.hidden = true;
      document.documentElement.style.overflow = '';
    };


    toggle.addEventListener('click', open);
    if (close) close.addEventListener('click', closeIt);
    overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeIt(); });
  }


  // ---------- Cart count live updates ----------
  function updateBadge(count) {
    const badges = document.querySelectorAll('[data-cart-count]');
    badges.forEach((b) => {
      b.textContent = count;
      if (count > 0) b.removeAttribute('hidden');
      else b.setAttribute('hidden', '');
    });
  }


  function initCartSync() {
    document.addEventListener('cart:refresh', refresh);
    document.addEventListener('cart:updated', refresh);
    const origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (...args) {
        const result = origFetch.apply(this, args);
        try {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
          if (/\/cart\/(add|change|update|clear)/.test(url)) {
            result.then(() => setTimeout(refresh, 60));
          }
        } catch (_) {}
        return result;
      };
    }
  }


  function refresh() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => updateBadge(cart.item_count))
      .catch(() => {});
  }


  // ---------- Predictive search (lightweight) ----------
  function initPredictive() {
    const form = document.querySelector('.hsg-header__search');
    if (!form) return;
    const input = form.querySelector('input[type="search"]');
    const panel = form.querySelector('[data-predictive-search]');
    if (!input || !panel) return;


    let timer = null;


    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) { panel.hidden = true; panel.innerHTML = ''; return; }
      timer = setTimeout(() => {
        fetch('/search/suggest.json?q=' + encodeURIComponent(q)
              + '&resources[type]=collection,page&resources[limit]=6')
          .then((r) => r.json())
          .then((data) => renderPredictive(panel, data, q))
          .catch(() => { panel.hidden = true; });
      }, 200);
    });


    document.addEventListener('click', (e) => {
      if (!form.contains(e.target)) { panel.hidden = true; }
    });
  }


  // ---------- Submit intercept: Enter → top predictive result ----------
  // Shopify's /search page cannot return collections (only products, pages,
  // articles). Since this store's search intent is schools (collections) and
  // states (pages), we intercept submit and navigate to whatever the predictive
  // API returns as the top match. Falls through to native submission if the
  // fetch fails so the user is never stuck.
  function initHeaderSubmit() {
    const form = document.querySelector('.hsg-header__search');
    if (!form) return;
    const input = form.querySelector('input[type="search"]');
    const panel = form.querySelector('[data-predictive-search]');
    if (!input) return;


    form.addEventListener('submit', (e) => {
      const q = input.value.trim();
      if (!q) { e.preventDefault(); return; }


      e.preventDefault();


      fetch('/search/suggest.json?q=' + encodeURIComponent(q)
            + '&resources[type]=collection,page&resources[limit]=6')
        .then((r) => r.json())
        .then((data) => {
          const r = (data && data.resources && data.resources.results) || {};
          const collections = r.collections || [];
          const pages = r.pages || [];
          const top = collections[0] || pages[0];
          if (top && top.url) {
            window.location.href = top.url;
          } else if (panel) {
            renderPredictive(panel, data, q);
          }
        })
        .catch(() => {
          // Network failure — let the native form submit as a last resort.
          form.submit();
        });
    });
  }


  function renderPredictive(panel, data, q) {
    const r = (data && data.resources && data.resources.results) || {};
    const collections = r.collections || [];
    const pages = r.pages || [];
    if (!collections.length && !pages.length) {
      panel.innerHTML = '<div style="padding:1rem;color:#666;">No schools or states found for &ldquo;' + escapeHtml(q) + '&rdquo;.</div>';
      panel.hidden = false;
      return;
    }
    let html = '<div style="padding:0.5rem;">';
    if (collections.length) {
      html += '<div style="padding:0.5rem 0.75rem;font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#888;">Schools</div>';
      collections.forEach((c) => {
        html += '<a href="' + escapeAttr(c.url) + '" style="display:block;padding:0.5rem 0.75rem;border-radius:8px;">' + escapeHtml(c.title) + '</a>';
      });
    }
    if (pages.length) {
      html += '<div style="padding:0.5rem 0.75rem;font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#888;">States</div>';
      pages.forEach((p) => {
        html += '<a href="' + escapeAttr(p.url) + '" style="display:block;padding:0.5rem 0.75rem;border-radius:8px;">' + escapeHtml(p.title) + '</a>';
      });
    }
    html += '</div>';
    panel.innerHTML = html;
    panel.hidden = false;
  }


  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }


  function escapeAttr(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }


  // ---------- Cart drawer trigger ----------
  function initCartDrawerTrigger() {
    const cartLink = document.querySelector('[data-cart-link]');
    if (!cartLink) return;
    cartLink.addEventListener('click', function (e) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;
      e.preventDefault();
      drawer.open(cartLink);
    });
  }


  function init() {
    initPlaceholderCycler();
    initSearchFocusClass();
    initMobileSearch();
    initCartSync();
    initCartDrawerTrigger();
    initPredictive();
    initHeaderSubmit();
  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();